var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, EmptyState, FlexBox, FormField, FormGrid, Page, SelectionList, SimplePagination, Text, TextWithHighlight, } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { integrationsApi } from 'apis/integrations';
import { webformEndpointsApi } from 'apis/webformEndpoints';
import { HookFormSelectionList, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { isResponseWithData } from 'utils/apis/axios';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { articles } from 'utils/elevio';
import { validateName } from 'utils/formValidator';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { SettingsIntegrationsDocumentationButton } from '../../../../components/documentationButton/documentationButton';
import { pathToWebformEndpointsSettings } from '../../webformEndpoints.view';
import styles from './webformEndpointsCreate.view.module.scss';
/** Settings page to create a new webform endpoint. */
export const WebformEndpointsCreateView = () => {
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const [filter, setFilter] = useState('');
    const { items: emails, asyncStatus: getEmailAsyncStatus, changeArg, nextPage, previousPage, hasNextPage, hasPreviousPage, } = usePaginatedEndpoint({
        endpoint: integrationsApi.endpoints.readEmailIntegrations,
        itemsSelector: ({ data: { emails } }) => emails || [],
        initialArg: {
            limit: 50,
            offset: 0,
            sort: { key: 'email_address', direction: 'asc' },
        },
    });
    const hasNoEmails = !emails.length;
    const [createWebformEndpoint, { isLoading: isCreating }] = webformEndpointsApi.useCreateWebformEndpointMutation();
    const { control, handleSubmit } = useForm({
        defaultValues: {
            emailIntegrationId: [],
            description: '',
        },
        mode: 'onChange',
    });
    const onSubmit = ({ emailIntegrationId, description, redirectUrl, }) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const response = yield createWebformEndpoint({
            description,
            email_integration_id: emailIntegrationId[0],
            redirect_uri: redirectUrl,
        }).unwrap();
        if (isResponseWithData(response)) {
            notify({
                title: 'Success',
                text: `"${(_a = response.data) === null || _a === void 0 ? void 0 : _a.description}" has been created.`,
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            navigate({ path: pathToWebformEndpointsSettings });
        }
        else {
            notify({
                title: 'Error while creating the webform endpoint.',
            }, NotificationType.Error);
        }
    });
    if (getEmailAsyncStatus === 'rejected') {
        return (_jsx(FlexBox, Object.assign({ justifyContent: "center", alignItems: "center" }, { children: "There was a problem getting the emails." })));
    }
    return (_jsx(Page.Section, Object.assign({ title: "Create webform endpoint", titleMeta: _jsx(SettingsIntegrationsDocumentationButton, { onClick: () => elevioClient.openArticle(articles.settings.webformEndpoints.documentation) }), controls: _jsx(Button, Object.assign({ isLoading: isCreating, disabled: isCreating || getEmailAsyncStatus === 'pending', onClick: handleSubmit(onSubmit) }, { children: "Save changes" })) }, { children: _jsx("form", Object.assign({ onSubmit: handleSubmit(onSubmit) }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormSelectionList, Object.assign({ control: control, label: "Email", name: 'emailIntegrationId', rules: {
                                required: {
                                    value: true,
                                    message: 'Please select an email',
                                },
                            }, helpIcon: {
                                dark: true,
                                title: 'Email address',
                                text: 'Choose the email address that will be used to route the conversations that you are creating from your webform.',
                            }, componentProps: {
                                clearable: true,
                                placeholder: 'Select email',
                                getItemTitleByValue: (email) => (_jsx(Text.Paragraph, { children: email })),
                                dropdownProps: {
                                    className: styles.selectDropdown,
                                },
                                filter: {
                                    value: filter,
                                    onChange: (event) => {
                                        const searchTerm = event.target.value;
                                        setFilter(searchTerm);
                                        changeArg({ searchTerm, offset: 0 }, {
                                            debounceTime: 300,
                                        });
                                    },
                                },
                                onToggleDropdown: (isOpen) => {
                                    // When the user closes the dropdown with a previous
                                    // search term, we reset so the email list gets fetched
                                    // once again
                                    if (!isOpen && filter !== '') {
                                        setFilter('');
                                        changeArg({ searchTerm: '', offset: 0 });
                                    }
                                },
                            } }, { children: _jsxs(_Fragment, { children: [emails.map((email) => (_jsx(SelectionList.Item, Object.assign({ value: email.email_address }, { children: filter ? (_jsx(TextWithHighlight, { text: email.email_address, highlight: filter })) : (_jsx(Text.Paragraph, { children: email.email_address })) }), email.email_address))), hasNoEmails && (_jsx(SelectionList.Item, Object.assign({ noFixedSelectionStateSpace: true, className: styles.selectDropdownRow }, { children: _jsx(EmptyState, { title: "No items found", description: "Try changing your search criteria." }) }))), _jsx(FlexBox, Object.assign({ className: styles.selectDropdownPagination }, { children: _jsx(SimplePagination, Object.assign({}, (getEmailAsyncStatus !== 'uninitialized' ||
                                            emails.length > 0
                                            ? {
                                                nextButtonClick: nextPage,
                                                previousButtonClick: previousPage,
                                                nextButtonDisabled: !hasNextPage || getEmailAsyncStatus !== 'fulfilled',
                                                previousButtonDisabled: !hasPreviousPage ||
                                                    getEmailAsyncStatus !== 'fulfilled',
                                            }
                                            : undefined))) }))] }) })) })), _jsx(FormField, { span: 3 }), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { control: control, label: "Name", name: 'description', rules: {
                                required: {
                                    value: true,
                                    message: 'Please add a name',
                                },
                                validate: validateName,
                            }, helpIcon: {
                                dark: true,
                                title: 'Endpoint name',
                                text: 'Type a name here, so you can recognize your webform integration easily later.',
                            }, componentProps: {
                                placeholder: 'Insert name',
                            } }) })), _jsx(FormField, { span: 3 }), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { control: control, label: "Redirect URL", name: 'redirectUrl', helpIcon: {
                                dark: true,
                                title: 'Redirect URL',
                                text: 'In case you are using the webform integration directly in your website code, please choose a redirect URL where the user should be directed to after submitting the webform.',
                            }, componentProps: {
                                placeholder: 'Insert redirect URL',
                            } }) })), _jsx(FormField, { span: 3 })] }) })) })));
};
